/**
 * Cloudflare Turnstile helper for checkout-gateway orders.
 * Whether a token is needed is decided per session (see turnstilePolicy.js);
 * this module only obtains one. turnstileSiteKey is required to render the
 * widget; without it only an active ts-bypass gets an order through.
 * Widget/script failure rejects; the caller decides whether that blocks the order.
 * Server-side siteverify outage fail-open is handled by checkout-gateway only.
 */
import $ from 'jquery';
import { getConfig } from './config';
import { debug } from './utils';
import {
	createExecuteWithRetry,
	DEFAULT_MAX_ATTEMPTS,
} from './turnstileExecute';

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const GATEWAY_CHALLENGE_CACHE_KEY = 'envisiaGwChallenge';
const GATEWAY_CHALLENGE_TTL_MS = 5 * 60 * 1000;
export const SCRIPT_LOAD_TIMEOUT_MS = 15000;
// Whole-token deadline: script load, render, challenge and retries. The retry
// limit only advances when the widget calls back, so a widget that never
// answers would otherwise leave "on" mode processing forever.
export const TOKEN_TIMEOUT_MS = 30000;

let scriptPromise = null;
let widgetId = null;
// The token request in progress. Starting a new one supersedes it: in
// "watch" mode the order goes after 5 s while the old request keeps running,
// and its 30 s deadline would otherwise remove a resubmit's widget.
let activeAttempt = null;

function hasSiteKey() {
	return Boolean(String(getConfig().turnstileSiteKey || '').trim());
}

// The site key is deliberately not part of the policy. "Enabled but no key"
// must resolve to "on" so getTurnstileToken blocks the order (or honors an
// active bypass); requiring the key here made a missing key silently turn
// Turnstile off and send every order without a token.
function turnstileEnabled() {
	return Boolean(getConfig().turnstileEnabled);
}

function gatewayBase() {
	return String(getConfig().checkoutGatewayURL || '').replace(/\/$/, '');
}

function readChallengeSkipCache() {
	try {
		const raw = sessionStorage.getItem(GATEWAY_CHALLENGE_CACHE_KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw);
		if (!parsed || typeof parsed.skip !== 'boolean' || !parsed.expiresAt) return null;
		if (Date.now() > parsed.expiresAt) return null;
		return parsed.skip;
	} catch (e) {
		return null;
	}
}

function writeChallengeSkipCache(skip) {
	try {
		sessionStorage.setItem(
			GATEWAY_CHALLENGE_CACHE_KEY,
			JSON.stringify({ skip: Boolean(skip), expiresAt: Date.now() + GATEWAY_CHALLENGE_TTL_MS })
		);
	} catch (e) {
		/* ignore quota / private mode */
	}
}

/**
 * Optional gateway probe before loading the challenge widget.
 * @returns {Promise<boolean>} true when the widget can be skipped
 */
function gatewayAllowsChallengeSkip() {
	const cached = readChallengeSkipCache();
	// Only trust a positive cache — never skip re-probe after a prior false/failure.
	if (cached === true) {
		return Promise.resolve(true);
	}
	const base = gatewayBase();
	if (!base) {
		return Promise.resolve(false);
	}
	// .done/.fail rather than .then/.catch: the engine runs on the page's
	// jQuery, and jQuery < 3 Deferreds have no .catch (it threw, so "on" mode
	// blocked every order and "watch" sent every order without a token).
	return new Promise((resolve) => {
		$.ajax({
			url: `${base}/ts-bypass/status`,
			method: 'GET',
			xhrFields: { withCredentials: true },
			timeout: 8000,
		})
			.done((data) => {
				const skip = Boolean(data && data.bypass === true);
				if (skip) {
					writeChallengeSkipCache(true);
				}
				resolve(skip);
			})
			.fail(() => resolve(false));
	});
}

function loadScript() {
	if (window.turnstile) {
		return Promise.resolve(window.turnstile);
	}
	if (scriptPromise) return scriptPromise;
	scriptPromise = new Promise((resolve, reject) => {
		let el = document.querySelector('script[data-envisia-turnstile]');
		// A stalled request fires neither load nor error. This bound fails it
		// fast (TOKEN_TIMEOUT_MS in getTurnstileToken is the overall backstop)
		// and, like any failure, drops the cached promise and the script tag so
		// the next submit retries with a fresh request instead of the dead one.
		const timer = setTimeout(() => fail(new Error('Turnstile script load timed out')), SCRIPT_LOAD_TIMEOUT_MS);
		const done = () => {
			clearTimeout(timer);
			resolve(window.turnstile);
		};
		function fail(err) {
			clearTimeout(timer);
			scriptPromise = null;
			if (el && el.parentNode) {
				el.parentNode.removeChild(el);
			}
			reject(err);
		}
		if (el) {
			el.addEventListener('load', done);
			el.addEventListener('error', () => fail(new Error('Turnstile script failed')));
			return;
		}
		el = document.createElement('script');
		el.src = SCRIPT_URL;
		el.async = true;
		el.dataset.envisiaTurnstile = '1';
		el.onload = done;
		el.onerror = () => fail(new Error('Turnstile script failed to load'));
		document.head.appendChild(el);
	});
	return scriptPromise;
}

function ensureHost() {
	let el = document.getElementById('envisia-turnstile-host');
	if (!el) {
		el = document.createElement('div');
		el.id = 'envisia-turnstile-host';
		// Invisible widgets need a mount node; keep footprint minimal (no off-screen "fake" checkbox).
		el.setAttribute('aria-hidden', 'true');
		document.body.appendChild(el);
	}
	return el;
}

function destroyWidget(turnstile) {
	if (widgetId != null && turnstile && typeof turnstile.remove === 'function') {
		try {
			turnstile.remove(widgetId);
		} catch (e) {
			/* ignore */
		}
	}
	widgetId = null;
}

function executeWithRetry(turnstile, action, onToken, onError) {
	const sitekey = String(getConfig().turnstileSiteKey || '').trim();
	const run = createExecuteWithRetry({
		turnstile,
		sitekey,
		action,
		ensureHost,
		setWidgetId: (id) => {
			widgetId = id;
		},
		destroyWidget,
		onToken,
		onError,
		maxAttempts: DEFAULT_MAX_ATTEMPTS,
		debug,
	});
	destroyWidget(turnstile);
	run();
	return run;
}

/**
 * Only called when the session's mode needs a token (on / watch).
 * @param {{ action?: string }} [opts]
 * @returns {Promise<string>} turnstile token, or '' when the bypass is active.
 *   Rejects when no token can be had — including when no turnstileSiteKey is
 *   configured and there is no bypass, so "on" never sends a tokenless order
 *   (acquireToken turns the rejection into '' for "watch").
 */
export function getTurnstileToken(opts = {}) {
	return gatewayAllowsChallengeSkip().then((skip) => {
		if (skip) {
			return '';
		}
		if (!hasSiteKey()) {
			throw new Error('Turnstile is required but turnstileSiteKey is not configured');
		}

		const action = opts.action || 'checkout';
		let runner = null;
		let expired = false;
		if (activeAttempt) {
			activeAttempt.supersede();
		}
		const challenge = loadScript().then(
			(turnstile) =>
				new Promise((resolve, reject) => {
					if (expired) {
						// The deadline already rejected; don't start a widget
						// nobody is waiting for.
						reject(new Error('Turnstile verification timed out'));
						return;
					}
					const finish = (token) => {
						if (!token) {
							reject(new Error('Turnstile returned empty token'));
							return;
						}
						resolve(token);
					};
					runner = executeWithRetry(turnstile, action, finish, reject);
				})
		);

		return new Promise((resolve, reject) => {
			const attempt = {};
			const stop = () => {
				expired = true;
				if (runner) {
					runner.cancel();
				}
			};
			const settle = (fn, value) => {
				clearTimeout(timer);
				if (activeAttempt === attempt) {
					activeAttempt = null;
				}
				fn(value);
			};
			const timer = setTimeout(() => {
				stop();
				destroyWidget(window.turnstile);
				settle(reject, new Error('Turnstile verification timed out'));
			}, TOKEN_TIMEOUT_MS);
			// Superseded by a newer request: stop quietly. The newer request
			// replaces the widget itself, so this one must not remove it.
			attempt.supersede = () => {
				stop();
				settle(reject, new Error('Turnstile request superseded'));
			};
			activeAttempt = attempt;
			challenge.then(
				(token) => settle(resolve, token),
				(err) => settle(reject, err)
			);
		});
	});
}

/** Local site config fallback, used when the gateway does not send a mode. */
export function isTurnstileEnabled() {
	return turnstileEnabled();
}
